import { createZodFormResolver, lt, showSuccessAlert, useFormControls } from '@mews/framework';
import { SchemaFormFields } from '@mews-commander/form';
import { OptimusProvider, OverlayedLoader } from '@mews-commander/ui';
import { Button, Stack } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { useRepositoryMutation, useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { getTaxWithholdFormDefinition, TaxWithholdingFormValues, taxWithholdSchema } from './formDefinition';

export const TaxWithholdingForm = () => {
    const { data } = useRepositorySuspenseQuery('accountingConfigurations', 'getWithheldTaxes', {});
    const { mutateAsync } = useRepositoryMutation('accountingConfigurations', 'updateWithheldTaxes', {
        invalidateQueries: {
            accountingConfigurations: ['getWithheldTaxes'],
        },
        onSuccess: () => {
            showSuccessAlert({ description: lt('SaveSuccessMessage') });
        },
    });

    const defaultValues = {
        taxWithholding: data.taxes?.filter((tax) => tax.isWithheld).map((tax) => tax.code) || [],
    } satisfies TaxWithholdingFormValues;

    const onSubmit = async (values: TaxWithholdingFormValues) => {
        await mutateAsync({
            withheldTaxCodes: values.taxWithholding,
        });
    };

    const formDefinition = getTaxWithholdFormDefinition(data);

    const { isSubmitting, renderFormContent } = useFormControls<TaxWithholdingFormValues>({
        onSubmit,
        defaultValues,
        resolver: createZodFormResolver(taxWithholdSchema),
    });

    return (
        <OptimusProvider density="spacious">
            <OverlayedLoader fixed show={isSubmitting} />
            {renderFormContent(
                <Stack vertical spacing="300">
                    <SchemaFormFields schema={taxWithholdSchema} formDefinition={formDefinition} />
                    <Stack alignment="end">
                        <Button data-test-id="submit" disabled={isSubmitting} type="submit" variant="primary">
                            {lt(TextKey.Save)}
                        </Button>
                    </Stack>
                </Stack>
            )}
        </OptimusProvider>
    );
};
